
import { Card } from '@/components/ui/card';
import { useFilterOptions } from './hooks/useFilterOptions';
import { SortByFilter } from './components/SortByFilter';
import { BestTimeFilter } from './components/BestTimeFilter';
import { BeachFilter } from './components/BeachFilter';
import { SafetyFilter } from './components/SafetyFilter';
import { RegionFilter } from './components/RegionFilter';
import { PriceRangeFilter } from './components/PriceRangeFilter';
import { ActiveFilters } from './components/ActiveFilters';

interface SearchFiltersProps {
  climate: string | null;
  hasBeach: boolean | null;
  safety: string | null;
  region: string | null;
  priceRange: [number, number];
  sortBy: string;
  costType: string;
  onFilterChange: (filter: string, value: any) => void;
  onClearFilters: () => void;
}

export const SearchFilters = ({
  climate,
  hasBeach,
  safety,
  region,
  priceRange,
  sortBy,
  costType = 'avg_accommodation_cost',
  onFilterChange,
  onClearFilters
}: SearchFiltersProps) => {
  const { bestTimeOptions } = useFilterOptions();

  // Improved price range change handler with validation
  const handlePriceRangeChange = (value: [number, number]) => {
    // Make sure we're working with a proper tuple of numbers
    if (!Array.isArray(value) || value.length !== 2) {
      console.error('Invalid price range value format:', value);
      return;
    }
    
    // Convert to numbers and ensure they're within valid ranges
    const min = typeof value[0] === 'number' ? value[0] : Number(value[0]);
    const max = typeof value[1] === 'number' ? value[1] : Number(value[1]);
    
    if (isNaN(min) || isNaN(max)) {
      console.error('Invalid price range values (NaN):', value);
      return;
    }
    
    // Determine max cap based on cost type
    let maxAllowed = 200; // default for avg_accommodation_cost
    if (costType === 'avg_meal_cost') maxAllowed = 100;
    else if (costType === 'avg_daily_budget') maxAllowed = 300;
    else if (costType === 'taxi_cost_per_km') maxAllowed = 20;
    
    // Create a valid range tuple
    const validRange: [number, number] = [
      Math.max(0, min),
      Math.min(maxAllowed, max)
    ];
    
    console.log('Sending price range to parent:', validRange);
    onFilterChange('priceRange', validRange);
  };

  const handleCostTypeChange = (value: string) => {
    console.log('Cost type change in SearchFilters:', value);
    onFilterChange('costType', value);
  };

  return (
    <Card className="p-6 space-y-6 bg-white rounded-lg shadow-sm sticky top-4">
      <SortByFilter 
        value={sortBy} 
        onChange={(value) => onFilterChange('sortBy', value)} 
      />
      
      <BestTimeFilter 
        value={climate}
        options={bestTimeOptions}
        onChange={(value) => onFilterChange('climate', value)}
      />
      
      <BeachFilter 
        value={hasBeach}
        onChange={(value) => onFilterChange('hasBeach', value)}
      />

      <SafetyFilter 
        value={safety}
        onChange={(value) => onFilterChange('safety', value)}
      />
      
      <RegionFilter 
        value={region}
        onChange={(value) => onFilterChange('region', value)}
      />
      
      <PriceRangeFilter 
        value={priceRange}
        onChange={handlePriceRangeChange}
        costType={costType}
        onCostTypeChange={handleCostTypeChange}
      />

      <ActiveFilters 
        climate={climate}
        hasBeach={hasBeach}
        safety={safety}
        region={region}
        onRemoveFilter={onFilterChange}
        onClearAll={onClearFilters}
      />
    </Card>
  );
};
